import { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Plus, Users, CalendarDays, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import { ShiftInstance } from '../services/api';

interface ShiftCalendarProps {
  instances: ShiftInstance[];
  onShiftClick: (instance: ShiftInstance) => void;
  onAddShift: (date: string) => void;
  onDateRangeChange: (startDate: string, endDate: string) => void;
}

// Format date as YYYY-MM-DD (using local time, not UTC)
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Get start of week (Monday)
function getWeekStart(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday = 1, Sunday = 0 -> -6
  d.setDate(d.getDate() + diff);
  return d;
}

// Add days to date (returns new Date)
function addDays(date: Date, days: number): Date {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  result.setDate(result.getDate() + days);
  return result;
}

// Get start of month
function getMonthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

// Get end of month
function getMonthEnd(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

// Format time for display
function formatTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' });
}

// Day and month names
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function ShiftCalendar({
  instances,
  onShiftClick,
  onAddShift,
  onDateRangeChange,
}: ShiftCalendarProps) {
  // Store year and month as numbers to avoid Date object mutation issues
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth());
  const [weekOffset, setWeekOffset] = useState(0); // Weeks from current week
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');

  // Use ref to track if we've done initial notification
  const hasNotifiedRef = useRef(false);

  // Calculate the reference date for the current view
  const currentDate = useMemo(() => {
    if (viewMode === 'week') {
      // Start from today, then add week offset
      const today = new Date();
      const todayWeekStart = getWeekStart(today);
      return addDays(todayWeekStart, weekOffset * 7);
    } else {
      // Month view uses year/month directly
      return new Date(year, month, 1);
    }
  }, [viewMode, year, month, weekOffset]);

  // Calculate week dates
  const weekDates = useMemo(() => {
    const start = getWeekStart(currentDate);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentDate]);

  // Calculate month dates (for month view)
  const monthDates = useMemo(() => {
    const monthStart = getMonthStart(currentDate);
    const monthEnd = getMonthEnd(currentDate);
    const calendarStart = getWeekStart(monthStart);

    const weeks: Date[][] = [];
    let weekStart = new Date(calendarStart);

    while (weekStart <= monthEnd || weeks.length < 5) {
      const week = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
      weeks.push(week);
      weekStart = addDays(weekStart, 7);
      if (weeks.length >= 6) break;
    }

    return weeks;
  }, [currentDate]);

  // Calculate date range and notify parent
  const getDateRange = (mode: 'week' | 'month', date: Date) => {
    if (mode === 'week') {
      const start = getWeekStart(date);
      const end = addDays(start, 6);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    } else {
      const monthStart = getMonthStart(date);
      const monthEnd = getMonthEnd(date);
      const calendarStart = getWeekStart(monthStart);
      return { startDate: formatDate(calendarStart), endDate: formatDate(monthEnd) };
    }
  };

  // Notify parent when date range changes
  useEffect(() => {
    const range = getDateRange(viewMode, currentDate);
    onDateRangeChange(range.startDate, range.endDate);
    hasNotifiedRef.current = true;
  }, [currentDate, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Normalize a date value (Date object or string) to YYYY-MM-DD string
  const normalizeDate = (dateValue: any): string => {
    if (!dateValue) return '';

    // If it's already a string in YYYY-MM-DD format
    if (typeof dateValue === 'string') {
      // Handle ISO strings like "2025-04-15T00:00:00.000Z"
      if (dateValue.includes('T')) {
        return dateValue.split('T')[0];
      }
      // Already in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
    }

    // If it's a Date object, format it as local date
    if (dateValue instanceof Date) {
      const year = dateValue.getFullYear();
      const month = String(dateValue.getMonth() + 1).padStart(2, '0');
      const day = String(dateValue.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    // Try to parse as date string
    try {
      const d = new Date(dateValue);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch {
      // Ignore parse errors
    }

    return '';
  };

  // Group instances by date
  const instancesByDate = useMemo(() => {
    const map: Record<string, ShiftInstance[]> = {};
    const seenIds = new Set<string>();

    for (const instance of instances) {
      if (seenIds.has(instance.id)) continue;
      seenIds.add(instance.id);

      const normalizedDate = normalizeDate(instance.date);
      if (!normalizedDate) continue;
      if (!map[normalizedDate]) map[normalizedDate] = [];
      map[normalizedDate].push(instance);
    }

    for (const date of Object.keys(map)) {
      map[date].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    }
    return map;
  }, [instances]);

  // Navigation handlers - simple, no closures over state
  const goToPrev = () => {
    if (viewMode === 'week') {
      setWeekOffset(prev => prev - 1);
    } else {
      // Go to previous month
      setMonth(prev => {
        if (prev === 0) {
          setYear(y => y - 1);
          return 11;
        }
        return prev - 1;
      });
    }
  };

  const goToNext = () => {
    if (viewMode === 'week') {
      setWeekOffset(prev => prev + 1);
    } else {
      // Go to next month
      setMonth(prev => {
        if (prev === 11) {
          setYear(y => y + 1);
          return 0;
        }
        return prev + 1;
      });
    }
  };

  const goToToday = () => {
    const today = new Date();
    setYear(today.getFullYear());
    setMonth(today.getMonth());
    setWeekOffset(0);
  };

  const handleViewModeChange = (mode: 'week' | 'month') => {
    if (mode === viewMode) return;

    // When switching modes, sync the state
    if (mode === 'month') {
      // Going to month view - set year/month from current week view
      const weekStart = weekDates[0];
      setYear(weekStart.getFullYear());
      setMonth(weekStart.getMonth());
    } else {
      // Going to week/day view - always return to the CURRENT week
      setWeekOffset(0);
    }

    setViewMode(mode);
  };

  // Get header text
  const headerText = useMemo(() => {
    if (viewMode === 'month') {
      return `${MONTH_NAMES[month]} ${year}`;
    }
    const start = weekDates[0];
    const end = weekDates[6];
    if (start.getMonth() === end.getMonth()) {
      return `${MONTH_NAMES[start.getMonth()]} ${start.getFullYear()}`;
    }
    return `${MONTH_NAMES[start.getMonth()]} - ${MONTH_NAMES[end.getMonth()]} ${end.getFullYear()}`;
  }, [weekDates, viewMode, month, year]);

  // Check if date is today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return formatDate(date) === formatDate(today);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between px-3 sm:px-4 py-3 gap-3 border-b border-slate-200 bg-slate-50/50">
        <div className="flex items-center justify-center sm:justify-start gap-2 sm:gap-3">
          <button
            onClick={goToPrev}
            className="p-2 sm:p-1.5 rounded-lg hover:bg-slate-200 active:bg-slate-300 transition-colors touch-manipulation"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 min-w-[140px] sm:min-w-[200px] text-center">
            {headerText}
          </h2>
          <button
            onClick={goToNext}
            className="p-2 sm:p-1.5 rounded-lg hover:bg-slate-200 active:bg-slate-300 transition-colors touch-manipulation"
          >
            <ChevronRight className="w-5 h-5 text-slate-600" />
          </button>
          <button
            onClick={goToToday}
            className="ml-1 sm:ml-2 px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 active:bg-slate-100 transition-colors touch-manipulation"
          >
            Today
          </button>
        </div>

        <div className="hidden sm:flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => handleViewModeChange('week')}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                viewMode === 'week'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              )}
            >
              Week
            </button>
            <button
              onClick={() => handleViewModeChange('month')}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                viewMode === 'month'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              )}
            >
              Month
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
        {viewMode === 'week' ? (
          /* Week View */
          <div className="grid grid-cols-7 divide-x divide-slate-200 min-w-[700px]">
            {/* Day Headers */}
            {weekDates.map((date, i) => (
              <div
                key={formatDate(date)}
                className={clsx(
                  'px-1.5 sm:px-2 py-2 text-center border-b border-slate-200',
                  isToday(date) ? 'bg-indigo-50' : 'bg-slate-50/50'
                )}
              >
                <div className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase">
                  {DAY_NAMES[i]}
                </div>
                <div
                  className={clsx(
                    'mt-0.5 sm:mt-1 text-base sm:text-lg font-semibold',
                    isToday(date) ? 'text-indigo-600' : 'text-slate-900'
                  )}
                >
                  {date.getDate()}
                </div>
              </div>
            ))}

            {/* Day Cells */}
            {weekDates.map((date) => {
              const dateStr = formatDate(date);
              const dayInstances = instancesByDate[dateStr] || [];

              return (
                <div
                  key={`cell-${dateStr}`}
                  className={clsx(
                    'min-h-[180px] sm:min-h-[200px] p-1.5 sm:p-2 space-y-1.5 sm:space-y-2',
                    isToday(date) ? 'bg-indigo-50/30' : ''
                  )}
                >
                  {dayInstances.map((instance) => (
                    <ShiftBlock
                      key={instance.id}
                      instance={instance}
                      onClick={() => onShiftClick(instance)}
                    />
                  ))}

                  <button
                    onClick={() => onAddShift(dateStr)}
                    className="w-full flex items-center justify-center gap-1 py-2 text-[10px] sm:text-xs font-medium text-slate-400 border border-dashed border-slate-200 rounded-lg hover:border-slate-300 hover:text-slate-500 hover:bg-slate-50 active:bg-slate-100 transition-colors touch-manipulation"
                  >
                    <Plus className="w-3 h-3" />
                    <span className="hidden sm:inline">Add Shift</span>
                    <span className="sm:hidden">Add</span>
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          /* Month View */
          <div className="min-w-[700px]">
            {/* Day Headers */}
            <div className="grid grid-cols-7 divide-x divide-slate-200">
              {DAY_NAMES.map((day) => (
                <div
                  key={day}
                  className="px-1.5 sm:px-2 py-2 text-center border-b border-slate-200 bg-slate-50/50"
                >
                  <div className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase">
                    {day}
                  </div>
                </div>
              ))}
            </div>

            {/* Week Rows */}
            {monthDates.map((week, weekIndex) => (
              <div key={weekIndex} className="grid grid-cols-7 divide-x divide-slate-200 border-b border-slate-200 last:border-b-0">
                {week.map((date) => {
                  const dateStr = formatDate(date);
                  const dayInstances = instancesByDate[dateStr] || [];
                  const isCurrentMonth = date.getMonth() === month;

                  return (
                    <div
                      key={dateStr}
                      className={clsx(
                        'min-h-[100px] sm:min-h-[120px] p-1 sm:p-1.5',
                        isToday(date) ? 'bg-indigo-50/30' : '',
                        !isCurrentMonth && 'bg-slate-50/50'
                      )}
                    >
                      <div
                        className={clsx(
                          'text-xs sm:text-sm font-medium mb-1',
                          isToday(date) ? 'text-indigo-600' : '',
                          !isCurrentMonth ? 'text-slate-400' : 'text-slate-700'
                        )}
                      >
                        {date.getDate()}
                      </div>

                      <div className="space-y-0.5">
                        {dayInstances.slice(0, 2).map((instance) => (
                          <button
                            key={instance.id}
                            onClick={() => onShiftClick(instance)}
                            className="w-full text-left px-1 py-0.5 rounded text-[9px] sm:text-[10px] font-medium truncate transition-colors hover:opacity-80"
                            style={{
                              backgroundColor: `${instance.color || '#3B82F6'}20`,
                              color: instance.color || '#3B82F6',
                            }}
                          >
                            {instance.name}
                          </button>
                        ))}
                        {dayInstances.length > 2 && (
                          <div className="text-[9px] sm:text-[10px] text-slate-500 px-1">
                            +{dayInstances.length - 2} more
                          </div>
                        )}
                      </div>

                      {isCurrentMonth && dayInstances.length === 0 && (
                        <button
                          onClick={() => onAddShift(dateStr)}
                          className="w-full flex items-center justify-center py-1 text-[9px] text-slate-400 hover:text-slate-500 transition-colors"
                        >
                          <Plus className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Shift Block Component
interface ShiftBlockProps {
  instance: ShiftInstance;
  onClick: () => void;
}

function ShiftBlock({ instance, onClick }: ShiftBlockProps) {
  const startTime = formatTime(instance.startTime);
  const endTime = formatTime(instance.endTime);
  const color = instance.color || '#3B82F6';

  const totalAssigned = instance.totalAssigned || 0;
  const totalRequired = instance.totalRequired || 0;
  const isFilled = instance.isFilled ?? (totalAssigned >= totalRequired);

  const hasUrgentNotes = instance.notes && (instance.notes.includes('NEED') || instance.notes.includes('?'));
  const hasEventNotes = instance.notes && !hasUrgentNotes;

  return (
    <button
      onClick={onClick}
      className={clsx(
        "w-full text-left p-1.5 sm:p-2 rounded-lg border transition-all hover:shadow-md active:scale-[0.98] touch-manipulation select-none",
        hasUrgentNotes && "ring-2 ring-amber-400 ring-offset-1"
      )}
      style={{
        backgroundColor: `${color}15`,
        borderColor: hasUrgentNotes ? '#F59E0B' : `${color}40`,
      }}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="flex items-center gap-1 flex-1 min-w-0">
          {hasUrgentNotes && (
            <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
          )}
          {hasEventNotes && (
            <CalendarDays className="w-3 h-3 text-blue-500 flex-shrink-0" />
          )}
          <span
            className="text-[10px] sm:text-xs font-semibold truncate"
            style={{ color }}
          >
            {instance.name}
          </span>
        </div>
        <div
          className={clsx(
            'flex items-center gap-0.5 px-1 sm:px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-medium flex-shrink-0',
            isFilled
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-amber-100 text-amber-700'
          )}
        >
          <Users className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
          {totalAssigned}/{totalRequired}
        </div>
      </div>
      <div className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-slate-500">
        {startTime} - {endTime}
      </div>
      {instance.notes && (
        <div className={clsx(
          "mt-1 text-[9px] sm:text-[10px] leading-tight line-clamp-2",
          hasUrgentNotes ? "text-amber-700 font-medium" : "text-slate-600"
        )}>
          {instance.notes}
        </div>
      )}
    </button>
  );
}
